import { useEffect, useMemo, useState } from "react";
import { useNavigate } from "react-router-dom";
import { supabase } from "../../../supabaseClient.jsx";
import { usePaymentGateway } from "../usePaymentGateway.js";
import { Button } from "../../../components/ui/button";
import { Card } from "../../../components/ui/card";
import { Badge } from "../../../components/ui/badge";

const formatCurrency = (value) =>
  new Intl.NumberFormat("pt-BR", {
    style: "currency",
    currency: "BRL",
  }).format(value ?? 0);

const formatDate = (value) =>
  new Date(value).toLocaleString("pt-BR", {
    dateStyle: "short",
    timeStyle: "short",
  });

const STATUS_CONFIG = {
  aprovado:  { label: "Aprovado",  variant: "default" },
  pendente:  { label: "Pendente",  variant: "secondary" },
  falhou:    { label: "Falhou",    variant: "destructive" },
  cancelado: { label: "Cancelado", variant: "outline" },
  estornado: { label: "Estornado", variant: "outline" },
};

function StatusBadge({ status }) {
  const cfg = STATUS_CONFIG[status] ?? { label: status, variant: "secondary" };
  return <Badge variant={cfg.variant}>{cfg.label}</Badge>;
}

export default function MeusPagamentosPage() {
  const navigate = useNavigate();
  const { gateway, provider } = usePaymentGateway();

  const [userId, setUserId] = useState(null);
  const [payments, setPayments] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    async function bootstrap() {
      const {
        data: { user },
      } = await supabase.auth.getUser();
      const mockUserId = localStorage.getItem("tesourinhos_mock_user_id");
      setUserId(user?.id || mockUserId || null);
    }
    bootstrap();
  }, []);

  useEffect(() => {
    async function loadPayments() {
      if (!userId) { setLoading(false); return; }
      setLoading(true);
      const result = await gateway.listPayments({ userId });
      setPayments(result || []);
      setLoading(false);
    }
    loadPayments();
  }, [gateway, userId]);

  async function handleUpdateStatus(transactionId, status) {
    const updated = await gateway.updatePaymentStatus({ transactionId, status });
    setPayments((prev) =>
      prev.map((payment) => (payment.id === transactionId ? updated : payment)),
    );
  }

  const hasPayments = useMemo(() => payments.length > 0, [payments]);

  return (
    <div className="mx-auto max-w-4xl px-4 py-10">
      <div className="mb-6 flex items-center justify-between gap-4">
        <div>
          <p className="mb-1 text-xs font-semibold uppercase tracking-wide text-muted-foreground">
            Histórico
          </p>
          <h1 className="text-2xl font-bold text-foreground">Meus pagamentos</h1>
        </div>
        <Button variant="outline" onClick={() => navigate(-1)}>
          Voltar
        </Button>
      </div>

      {loading && (
        <div className="space-y-3">
          {[1, 2, 3].map((i) => (
            <Card key={i} className="p-5">
              <div className="h-4 w-48 rounded bg-muted animate-pulse mb-2" />
              <div className="h-3 w-32 rounded bg-muted animate-pulse" />
            </Card>
          ))}
        </div>
      )}

      {!loading && !hasPayments && (
        <Card className="p-6 text-center">
          <p className="text-sm text-muted-foreground">Nenhum pagamento encontrado.</p>
        </Card>
      )}

      {!loading && hasPayments && (
        <div className="space-y-3">
          {payments.map((payment) => (
            <Card key={payment.id} className="p-5">
              <div className="flex flex-wrap items-start justify-between gap-3">
                <div className="min-w-0">
                  <h2 className="text-sm font-semibold text-foreground truncate">
                    {payment.items?.[0]?.nome || "Pagamento"}
                  </h2>
                  <p className="text-xs text-muted-foreground mt-0.5 truncate">{payment.id}</p>
                </div>
                <strong className="text-foreground shrink-0">
                  {formatCurrency(payment.total)}
                </strong>
              </div>

              <div className="mt-3 flex flex-wrap items-center gap-x-4 gap-y-2 text-xs text-muted-foreground">
                <StatusBadge status={payment.status} />
                <span>
                  Método:{" "}
                  <strong className="text-foreground">{payment.paymentMethod?.toUpperCase()}</strong>
                </span>
                <span>{formatDate(payment.createdAt)}</span>
              </div>

              {provider === "mock" && payment.status === "pendente" && (
                <div className="mt-4 flex gap-2">
                  <Button
                    size="sm"
                    onClick={() => handleUpdateStatus(payment.id, "aprovado")}
                  >
                    Aprovar
                  </Button>
                  <Button
                    size="sm"
                    variant="destructive"
                    onClick={() => handleUpdateStatus(payment.id, "falhou")}
                  >
                    Reprovar
                  </Button>
                </div>
              )}
            </Card>
          ))}
        </div>
      )}
    </div>
  );
}
